import { socialUrlPattern } from '@/constants/regex'
import { SOCIAL_BRANDS, SocialBrand, normalizeSocialType } from '@/constants/socialBrands'
import {
  VCardSharedLinkTypes,
  VCardSocialLinkTypes,
  TemplateModeTypes,
  EmailSignatureTypes,
  VCardStatusTypes,
} from '@/lib/types/VCardT'
import { GuideStep, MapperType } from '@/lib/types/utilsT'
import { IconTypes } from '@/components/Icon'
import { MultiSelectItem } from '@/components/MultiSelect'
import { getCountryFromDate } from '@/lib/helpers/country'

/**
 * Every network, ready to render: the brand row plus the URL rule built from its domains
 * and the white-glyph asset the e-mail signature embeds.
 */
export type VCardSocialField = SocialBrand & { pattern: RegExp; svgImg: string }

export const VCARD_SOCIALS: VCardSocialField[] = SOCIAL_BRANDS.map((brand) => ({
  ...brand,
  pattern: socialUrlPattern(brand.domains),
  svgImg: `/social/${brand.type}.svg`,
}))

export const VCARD_SOCIALS_BY_TYPE = Object.fromEntries(
  VCARD_SOCIALS.map((social) => [social.type, social])
) as Record<VCardSocialLinkTypes, VCardSocialField | undefined>

/** Resolves a stored type — legacy names included — to the row that renders it. */
export const getVCardSocial = (type: string): VCardSocialField | undefined =>
  VCARD_SOCIALS_BY_TYPE[normalizeSocialType(type)]

export const SOCIAL_PATTERNS: Record<string, RegExp> = VCARD_SOCIALS.reduce(
  (acc, link) => {
    acc[link.type] = link.pattern
    return acc
  },
  {} as Record<string, RegExp>
)

// TODO: uncomment when we will have a design for two color pickers
// Preset color swatches
export const COLOR_PRESETS = [
  {
    name: 'Royal Blue',
    primary: '#4169E1',
    // secondary: '#87CEEB'
  },
  {
    name: 'Forest Green',
    primary: '#228B22',
    // secondary: '#90EE90'
  },
  {
    name: 'Coffee Brown',
    primary: '#8B4513',
    // secondary: '#D2B48C'
  },
  {
    name: 'Purple',
    primary: '#8A2BE2',
    // secondary: '#DDA0DD'
  },
  {
    name: 'Crimson',
    primary: '#D7263D',
  },
  {
    name: 'Coral',
    primary: '#FF7F50',
    // secondary: '#FFB6C1'
  },
  {
    name: 'Teal',
    primary: '#008080',
    // secondary: '#40E0D0'
  },
  {
    name: 'Orange',
    primary: '#FF8C00',
    // secondary: '#FFD700'
  },
  {
    name: 'Pink',
    primary: '#FF1493',
    // secondary: '#FFC0CB'
  },
  {
    name: 'Slate',
    primary: '#334155',
  },
]

export const DEFAULT_ADDRESS_FIELD = {
  label: 'Work',
  street1: '',
  street2: '',
  city: '',
  state: '',
  postal: '',
  country: getCountryFromDate(),
}

/** Banner shipped with every card until the owner uploads their own. */
export const DEFAULT_VCARD_BANNER = '/images/btm-gradient.png'

export const DEFAULT_VCARD_FORM_VALUES = {
  avatar: null,
  banner: null,
  representativeName: '',
  firstName: '',
  lastName: '',
  industry: 'other',
  mainColor: '#3A72F3',
  notes: '',
  emails: [{ alias: '', value: '' }],
  jobPosition: '',
  website: '',
  addresses: [DEFAULT_ADDRESS_FIELD],
  unverifiedPhones: [{ label: '', phone: '' }],
  defaultLinks: [],
  socialLinks: [],
  companyFileItems: [],
  taxonomy: [],
}

export type VCardSharedLinkType = MapperType<VCardSharedLinkTypes>
export const VCARD_SHARED_LINK_TYPES = {
  LIVELINK: 'liveLink',
  LIVEQRCODE: 'liveQrCode',
  PREVIEWLINK: 'previewLink',
  PREVIEWQRCODE: 'previewQrCode',
  LIVEDIRECTDOWNLOAD: 'liveDirectDownload',
  LIVENFC: 'liveNfc',
  PREVIEWNFC: 'previewNfc',
} satisfies VCardSharedLinkType

export type TemplateModeType = MapperType<TemplateModeTypes>
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
} satisfies TemplateModeType

export const NFC_SETUP_STEPS: {
  id: number
  title: string
  description: string
  icon: IconTypes
  content: 'url' | 'qr' | 'app' | 'info'
}[] = [
  {
    id: 1,
    title: 'Download NFC Tools',
    description: 'Install the NFC Tools app from your device app store to write data to NFC tags.',
    icon: 'download',
    content: 'app',
  },
  {
    id: 2,
    title: 'Open NFC Tools',
    description: 'Launch the app and navigate to the "Write" tab to begin programming your tag.',
    icon: 'phone',
    content: 'info',
  },
  {
    id: 3,
    title: 'Add URL Record',
    description: 'Select "Add a record" and choose "URL / URI" from the available options.',
    icon: 'link',
    content: 'info',
  },
  {
    id: 4,
    title: 'Copy Contact URL',
    description: 'Copy your unique contact URL below and paste it into the NFC Tools app.',
    icon: 'copy',
    content: 'url',
  },
  {
    id: 5,
    title: 'Write to NFC Tag',
    description: 'Tap "Write" in the app and hold your NFC tag close to your device.',
    icon: 'barcode',
    content: 'info',
  },
  {
    id: 6,
    title: 'Test Your Tag',
    description: 'Tap your phone to the NFC tag to verify it opens your contact card.',
    icon: 'check',
    content: 'qr',
  },
]

export const VCARD_SAVE_GUIDE_STEPS = {
  // iOS - Safari (primary browser on iOS)
  'ios-safari': {
    title: 'Save Contact on iPhone (Safari)',
    icon: 'phone' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'The contact file will download automatically',
      },
      {
        number: 2,
        text: 'Tap the download icon in the Safari toolbar (top right)',
      },
      {
        number: 3,
        text: 'Tap on the downloaded .vcf file',
      },
      {
        number: 4,
        text: 'Tap "Add Contact" or "Create New Contact" to save',
      },
    ],
  },
  'ios-chrome': {
    title: 'Save Contact on iPhone (Chrome)',
    icon: 'phone' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Tap the downloads icon at the bottom of Chrome',
      },
      {
        number: 2,
        text: 'Find and tap the .vcf file',
      },
      {
        number: 3,
        text: 'Select "Open in Contacts" or "Add to Contacts"',
      },
      {
        number: 4,
        text: 'Tap "Create New Contact" to save',
      },
    ],
  },
  'ios-duckduckgo': {
    title: 'Save Contact on iPhone (DuckDuckGo)',
    icon: 'phone' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Tap the menu icon (three lines) at the bottom',
      },
      {
        number: 2,
        text: 'Tap "Downloads" to see your files',
      },
      {
        number: 3,
        text: 'Tap on the .vcf file',
      },
      {
        number: 4,
        text: 'Select "Add to Contacts" and tap "Create New Contact"',
      },
    ],
  },
  'ios-other': {
    title: 'Save Contact on iPhone',
    icon: 'phone' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Find the downloaded .vcf file in your downloads',
      },
      {
        number: 2,
        text: 'Tap on the file to open it',
      },
      {
        number: 3,
        text: 'Select "Add to Contacts" or "Import"',
      },
      {
        number: 4,
        text: 'Tap "Create New Contact" to save',
      },
    ],
  },

  // Android - Chrome (most common)
  'android-chrome': {
    title: 'Save Contact on Android (Chrome)',
    icon: 'phone' as const,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'fill-green-500',
    steps: [
      {
        number: 1,
        text: 'Swipe down to see the download notification, or tap the downloads icon in Chrome',
      },
      {
        number: 2,
        text: 'Tap "Open" on the notification or tap the .vcf file',
      },
      {
        number: 3,
        text: 'Your Contacts app will open automatically',
      },
      {
        number: 4,
        text: 'Tap "Import" or "Save" to add the contact',
      },
    ],
  },
  'android-firefox': {
    title: 'Save Contact on Android (Firefox)',
    icon: 'phone' as const,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'fill-green-500',
    steps: [
      {
        number: 1,
        text: 'Tap the download arrow icon in Firefox',
      },
      {
        number: 2,
        text: 'Tap on the downloaded .vcf file',
      },
      {
        number: 3,
        text: 'Select "Contacts" or your preferred contacts app',
      },
      {
        number: 4,
        text: 'Tap "Import" to add the contact',
      },
    ],
  },
  'android-duckduckgo': {
    title: 'Save Contact on Android (DuckDuckGo)',
    icon: 'phone' as const,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'fill-green-500',
    steps: [
      {
        number: 1,
        text: 'Swipe down to see the download notification',
      },
      {
        number: 2,
        text: 'Tap "Open" on the notification, or open the menu and tap "Downloads"',
      },
      {
        number: 3,
        text: 'Tap on the .vcf file to open it',
      },
      {
        number: 4,
        text: 'Select "Import" or "Save" to add the contact',
      },
    ],
  },
  'android-other': {
    title: 'Save Contact on Android',
    icon: 'phone' as const,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'fill-green-500',
    steps: [
      {
        number: 1,
        text: 'Open your "Downloads" folder or check notifications',
      },
      {
        number: 2,
        text: 'Tap on the downloaded .vcf file',
      },
      {
        number: 3,
        text: 'Select "Import" or "Add to Contacts"',
      },
      {
        number: 4,
        text: 'The contact will be saved to your device',
      },
    ],
  },

  // Windows - Chrome
  'windows-chrome': {
    title: 'Save Contact on Windows (Chrome)',
    icon: 'container' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the bottom-left of Chrome or press Ctrl+J',
      },
      {
        number: 2,
        text: 'Click "Show in folder" to open Downloads',
      },
      {
        number: 3,
        text: 'Double-click the .vcf file',
      },
      {
        number: 4,
        text: 'It will open in Outlook or People app - click "Save" to import',
      },
    ],
  },
  'windows-edge': {
    title: 'Save Contact on Windows (Edge)',
    icon: 'container' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Press Ctrl+J to open downloads or click the downloads icon',
      },
      {
        number: 2,
        text: 'Click "Show in folder" next to the .vcf file',
      },
      {
        number: 3,
        text: 'Double-click the file to open it',
      },
      {
        number: 4,
        text: 'The People app or Outlook will open - click "Save" to add the contact',
      },
    ],
  },
  'windows-firefox': {
    title: 'Save Contact on Windows (Firefox)',
    icon: 'container' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Click the download arrow icon in the Firefox toolbar',
      },
      {
        number: 2,
        text: 'Right-click on the .vcf file and select "Open Containing Folder"',
      },
      {
        number: 3,
        text: 'Double-click the file to open it',
      },
      {
        number: 4,
        text: 'The file will open in Outlook or People app - click "Save"',
      },
    ],
  },
  'windows-duckduckgo': {
    title: 'Save Contact on Windows (DuckDuckGo)',
    icon: 'container' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the bottom-left of DuckDuckGo or press Ctrl+J',
      },
      {
        number: 2,
        text: 'Click "Show in folder" to open Downloads',
      },
      {
        number: 3,
        text: 'Double-click the .vcf file',
      },
      {
        number: 4,
        text: 'It will open in Outlook or People app - click "Save" to import',
      },
    ],
  },
  'windows-other': {
    title: 'Save Contact on Windows',
    icon: 'container' as const,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'fill-blue-500',
    steps: [
      {
        number: 1,
        text: 'Locate the .vcf file in your Downloads folder',
      },
      {
        number: 2,
        text: 'Double-click the file to open it',
      },
      {
        number: 3,
        text: 'The file will open in Outlook or the People app',
      },
      {
        number: 4,
        text: 'Click "Save" or "Import" to add the contact',
      },
    ],
  },

  // macOS - Safari (default)
  'macos-safari': {
    title: 'Save Contact on Mac (Safari)',
    icon: 'container' as const,
    iconBg: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'fill-gray-500',
    steps: [
      {
        number: 1,
        text: 'Click the downloads icon in Safari toolbar or press ⌘+Option+L',
      },
      {
        number: 2,
        text: 'Click the magnifying glass next to the .vcf file',
      },
      {
        number: 3,
        text: 'Double-click the file to open in Contacts app',
      },
      {
        number: 4,
        text: 'Click "Add to Contacts" to save',
      },
    ],
  },
  'macos-chrome': {
    title: 'Save Contact on Mac (Chrome)',
    icon: 'container' as const,
    iconBg: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'fill-gray-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the top-right of Chrome or press Shift+⌘+J',
      },
      {
        number: 2,
        text: 'Click "Show in Finder" to locate the file',
      },
      {
        number: 3,
        text: 'Double-click the .vcf file',
      },
      {
        number: 4,
        text: 'Contacts app will open - click "Add to Contacts"',
      },
    ],
  },
  'macos-firefox': {
    title: 'Save Contact on Mac (Firefox)',
    icon: 'container' as const,
    iconBg: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'fill-gray-500',
    steps: [
      {
        number: 1,
        text: 'Click the download arrow icon in Firefox',
      },
      {
        number: 2,
        text: 'Right-click the .vcf file and select "Show in Finder"',
      },
      {
        number: 3,
        text: 'Double-click the file to open it',
      },
      {
        number: 4,
        text: 'Contacts app will open automatically - click "Add to Contacts"',
      },
    ],
  },
  'macos-duckduckgo': {
    title: 'Save Contact on Mac (DuckDuckGo)',
    icon: 'container' as const,
    iconBg: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'fill-gray-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the bottom-left of DuckDuckGo or press Shift+⌘+J',
      },
      {
        number: 2,
        text: 'Click "Show in Finder" to locate the file',
      },
      {
        number: 3,
        text: 'Double-click the .vcf file',
      },
      {
        number: 4,
        text: 'Contacts app will open - click "Add to Contacts"',
      },
    ],
  },
  'macos-other': {
    title: 'Save Contact on Mac',
    icon: 'container' as const,
    iconBg: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'fill-gray-500',
    steps: [
      {
        number: 1,
        text: 'Locate the .vcf file in your Downloads folder',
      },
      {
        number: 2,
        text: 'Double-click the file to open it',
      },
      {
        number: 3,
        text: 'The Contacts app will open automatically',
      },
      {
        number: 4,
        text: 'Click "Add to Contacts" to save',
      },
    ],
  },

  // Linux
  'linux-chrome': {
    title: 'Save Contact on Linux (Chrome)',
    icon: 'container' as const,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'fill-orange-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the bottom of Chrome or press Ctrl+J',
      },
      {
        number: 2,
        text: 'Click "Show in folder" to open your file manager',
      },
      {
        number: 3,
        text: 'Open your contacts app (Evolution, Contacts, or Thunderbird)',
      },
      {
        number: 4,
        text: 'Go to File > Import and select the .vcf file',
      },
    ],
  },
  'linux-firefox': {
    title: 'Save Contact on Linux (Firefox)',
    icon: 'container' as const,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'fill-orange-500',
    steps: [
      {
        number: 1,
        text: 'Click the download arrow in Firefox toolbar',
      },
      {
        number: 2,
        text: 'Right-click the .vcf file and select "Open Containing Folder"',
      },
      {
        number: 3,
        text: 'Open your contacts manager (Evolution, Contacts, etc.)',
      },
      {
        number: 4,
        text: 'Go to File > Import or drag the .vcf file into the app',
      },
    ],
  },
  'linux-duckduckgo': {
    title: 'Save Contact on Linux (DuckDuckGo)',
    icon: 'container' as const,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'fill-orange-500',
    steps: [
      {
        number: 1,
        text: 'Click the download at the bottom of DuckDuckGo or press Ctrl+J',
      },
      {
        number: 2,
        text: 'Click "Show in folder" to open your file manager',
      },
      {
        number: 3,
        text: 'Open your contacts app (Evolution, Contacts, or Thunderbird)',
      },
      {
        number: 4,
        text: 'Go to File > Import and select the .vcf file',
      },
    ],
  },
  'linux-other': {
    title: 'Save Contact on Linux',
    icon: 'container' as const,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'fill-orange-500',
    steps: [
      {
        number: 1,
        text: 'Locate the .vcf file in your Downloads folder',
      },
      {
        number: 2,
        text: 'Open your contacts manager (Evolution, Contacts, or Thunderbird)',
      },
      {
        number: 3,
        text: 'Go to File > Import or right-click and select "Import vCard"',
      },
      {
        number: 4,
        text: 'Select the .vcf file and confirm to add the contact',
      },
    ],
  },
}

export const CONTACT_GUIDE_STEPS: GuideStep[] = [
  {
    id: 1,
    title: 'Welcome to Your Contact Page!',
    description:
      'This is your digital business card. Share it with anyone, and they can save your contact information instantly to their device.',
    position: 'center',
    icon: 'card',
  },
  {
    id: 2,
    title: 'View Contact Details',
    description:
      'Here you can see all the contact information including name, job position, email, phone numbers, and social media links.',
    target: '.contact-card-preview',
    position: 'center',
    icon: 'user-check',
  },
  {
    id: 3,
    title: 'Download Contact',
    description:
      'Tap this button to download the contact as a .vcf file. You can then easily add it to your phone contacts or email app.',
    target: 'button[aria-label="Download Contact"]',
    position: 'top-center',
    icon: 'download',
  },
  {
    id: 4,
    title: 'View Saving Instructions',
    description:
      'CTap this button to open a guide showing how to save the contact on your device. Instructions may look slightly different depending on your device and browser.',
    target: 'button[aria-label="How to save contact"]',
    position: 'bottom-right',
    icon: 'phone',
  },
  {
    id: 5,
    title: 'Share This Page',
    description:
      "You can share this page's URL with anyone! They'll be able to view and download your contact information instantly.",
    position: 'center',
    icon: 'share',
  },
]

export type EmailSignatureType = MapperType<EmailSignatureTypes>
// Order is the order the style chooser renders them in.
export const EMAIL_SIGNATURE_TYPE = {
  MINIMAL: 'minimal',
  CLASSIC: 'classic',
  MODERN: 'modern',
  VERTICAL: 'vertical',
  CENTERED: 'centered',
  EDITORIAL: 'editorial',
} satisfies EmailSignatureType

export const EMAIL_SIGNATURE_TYPE_CONFIG: Record<
  EmailSignatureTypes,
  { label: string; description: string }
> = {
  [EMAIL_SIGNATURE_TYPE.MINIMAL]: {
    label: 'Text only',
    description: 'No mark, no rule — details only',
  },
  [EMAIL_SIGNATURE_TYPE.CLASSIC]: {
    label: 'Mark left',
    description: 'Mark, rule, then the details',
  },
  [EMAIL_SIGNATURE_TYPE.MODERN]: {
    label: 'Banner',
    description: 'Mark and name on top, details full width',
  },
  [EMAIL_SIGNATURE_TYPE.VERTICAL]: {
    label: 'Stacked',
    description: 'One narrow column, mark on top',
  },
  [EMAIL_SIGNATURE_TYPE.CENTERED]: {
    label: 'Centred',
    description: 'The stack, on the centre line',
  },
  [EMAIL_SIGNATURE_TYPE.EDITORIAL]: {
    label: 'Mark right',
    description: 'Details lead, mark closes the block',
  },
}

export const COLLECTABLE_FIELDS = {
  FIRST_NAME: 'first_name',
  PHONE: 'phone',
  EMAIL: 'email',
}
export const COLLECTABLE_FIELDS_CONFIG: Record<
  (typeof COLLECTABLE_FIELDS)[keyof typeof COLLECTABLE_FIELDS],
  { fieldKey: string }
> = {
  [COLLECTABLE_FIELDS.FIRST_NAME]: {
    fieldKey: 'firstName',
  },
  [COLLECTABLE_FIELDS.PHONE]: {
    fieldKey: 'phone',
  },
  [COLLECTABLE_FIELDS.EMAIL]: {
    fieldKey: 'email',
  },
}

export const AVAILABLE_COLLECTABLE_FIELDS: MultiSelectItem[] = [
  { title: 'Name', icon: 'user-round', value: COLLECTABLE_FIELDS.FIRST_NAME },
  { title: 'Phone', icon: 'phone', value: COLLECTABLE_FIELDS.PHONE },
  { title: 'Email', icon: 'mail', value: COLLECTABLE_FIELDS.EMAIL },
]

export type VCardStatusType = MapperType<VCardStatusTypes>
export const VCARD_STATUSES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  AUTO_DEACTIVATED: 'auto-deactivated',
  MANUALLY_DEACTIVATED: 'manually-deactivated',
} satisfies VCardStatusType

export const VCARD_STATUSES_CONFIG: Record<
  VCardStatusTypes,
  {
    label: string
    description?: string
    icon?: IconTypes
    bgColor?: string
    textColor?: string
    dotColor?: string
  }
> = {
  [VCARD_STATUSES.DRAFT]: {
    label: 'Draft',
    description: 'Card is saved but not yet active',
    icon: 'edit',
    bgColor: 'bg-n-3/15 dark:bg-n-4/25',
    textColor: 'text-n-6 dark:text-n-3',
    dotColor: 'bg-n-4',
  },

  [VCARD_STATUSES.PUBLISHED]: {
    label: 'Activated',
    description: 'Card is live and ready to share',
    icon: 'check',
    bgColor: 'bg-green-500/15 dark:bg-green-500/20',
    textColor: 'text-green-700 dark:text-green-300',
    dotColor: 'bg-green-500',
  },

  [VCARD_STATUSES.AUTO_DEACTIVATED]: {
    label: 'Auto Deactivated',
    description: 'Card was automatically deactivated',
    icon: 'pause',
    bgColor: 'bg-yellow-500/15 dark:bg-yellow-500/20',
    textColor: 'text-yellow-700 dark:text-yellow-300',
    dotColor: 'bg-yellow-500',
  },

  [VCARD_STATUSES.MANUALLY_DEACTIVATED]: {
    label: 'Deactivated',
    description: 'Card was manually deactivated',
    icon: 'close',
    bgColor: 'bg-red-500/15 dark:bg-red-500/20',
    textColor: 'text-red-700 dark:text-red-300',
    dotColor: 'bg-red-500',
  },
}

export const CREATE_VCARD_DROPDOWN = [
  {
    label: 'as Draft',
    pointColor: VCARD_STATUSES_CONFIG['draft'].dotColor,
    value: VCARD_STATUSES.DRAFT,
  },
  {
    label: 'as Active',
    pointColor: VCARD_STATUSES_CONFIG['published'].dotColor,
    value: VCARD_STATUSES.PUBLISHED,
  },
]

export const ADDRESS_LABELS = [
  'Work',
  'Home',
  'School',
  'Headquarters',
  'Billing',
  'Shipping',
  'Other',
] as const

export const PHONE_SUGGESTION_LABELS = ['Call', 'Message', 'Phone', 'Work', 'Mobile', 'Main']

export const EMAIL_SUGGESTION_LABELS = ['Work', 'Personal', 'Home', 'Other']

export type FieldDef = {
  key: string
  label: string
  /** Social rows carry `socialType` instead and render their brand mark. */
  icon?: IconTypes
  socialType?: VCardSocialLinkTypes
  nonRemovable?: boolean
}

export const CARD_FIELD_DEFINITIONS: { category: string; fields: FieldDef[] }[] = [
  {
    category: 'Personal',
    fields: [
      { key: 'representativeName', label: 'Name', icon: 'profile', nonRemovable: true },
      { key: 'industry', label: 'Industry', icon: 'building', nonRemovable: true },
      { key: 'notes', label: 'Description', icon: 'note' },
      { key: 'website', label: 'Website', icon: 'web' },
    ],
  },
  {
    category: 'General',
    fields: [
      { key: 'emails', label: 'Email', icon: 'email' },
      { key: 'unverifiedPhones', label: 'Phone', icon: 'phone' },
      { key: 'defaultLinks', label: 'Links', icon: 'link' },
      { key: 'addresses', label: 'Address', icon: 'marker' },
      { key: 'companyFileItems', label: 'Files', icon: 'file' },
    ],
  },
  {
    category: 'Social',
    fields: VCARD_SOCIALS.map((social) => ({
      key: `social_${social.type}`,
      label: social.name,
      socialType: social.type,
    })),
  },
]

export const ALL_FIELD_DEFS = CARD_FIELD_DEFINITIONS.flatMap((cat) => cat.fields)
